import { useState } from "react";
import { motion } from "framer-motion";
import { Building2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { ProjectSketch } from "./ProjectSketch";
import { useLang } from "@/i18n";

const RefRow = ({ index, title, place, period, sketch }) => {
  const [hover, setHover] = useState(false);
  const num = String(index + 1).padStart(2, "0");
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative border-b border-[var(--line)] first:border-t"
      data-testid={`reference-row-${index}`}
    >
      <motion.div
        className="absolute inset-0 bg-[var(--bg-2)]"
        initial={false}
        animate={{ opacity: hover ? 1 : 0 }}
        transition={{ duration: 0.4 }}
      />
      <div className="relative flex items-center gap-4 md:gap-8 py-6 md:py-8 px-1 md:px-4">
        <span className="font-mono2 text-xs md:text-sm text-[var(--txt-mut)] w-7 shrink-0 self-start pt-1">
          {num}
        </span>
        <motion.span
          className="shrink-0"
          animate={{ color: hover ? "#d9cfb8" : "#6f6a5f" }}
          transition={{ duration: 0.4 }}
        >
          <Building2 className="h-5 w-5 md:h-6 md:w-6" strokeWidth={1.3} />
        </motion.span>
        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-baseline md:justify-between gap-1.5 md:gap-10">
          <motion.h3
            className="font-heading font-bold text-2xl md:text-4xl tracking-tight break-words hyphens-auto"
            animate={{ x: hover ? 14 : 0, color: hover ? "#d9cfb8" : "#f2f0eb" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {title}
            {/* Mobile: Skizze unter dem Titel */}
            <span className="md:hidden mt-4 block w-40 max-w-full" data-testid={`reference-sketch-m-${index}`}>
              <ProjectSketch variant={sketch} hover={hover} />
            </span>
          </motion.h3>
          <span className="font-mono2 text-[11px] md:text-sm tracking-[0.15em] uppercase text-[var(--txt-dim)] shrink-0 md:text-right">
            {place} · {period}
          </span>
        </div>
        {/* Desktop: Skizze rechts */}
        <div
          className="hidden md:block w-[150px] lg:w-[180px] shrink-0"
          data-testid={`reference-sketch-${index}`}
        >
          <ProjectSketch variant={sketch} hover={hover} />
        </div>
      </div>
    </div>
  );
};

export const References = () => {
  const { t } = useLang();
  const r = t.references;
  return (
    <section
      id="referenzen"
      className="relative border-t border-[var(--line)] bg-[var(--bg-0)] py-24 md:py-36"
      data-testid="references-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
          <div>
            <Reveal>
              <span className="font-mono2 text-[11px] tracking-[0.4em] uppercase text-[var(--txt-mut)]">
                {r.label}
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-heading font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl mt-6 leading-[0.95]">
                {r.heading}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="font-body font-light text-[var(--txt-dim)] text-base md:max-w-xs">
              {r.intro}
            </p>
          </Reveal>
        </div>

        <div data-testid="references-list">
          {r.items.map(([title, place, period], i) => (
            <Reveal key={i} delay={Math.min(i * 0.06, 0.3)} y={20}>
              <RefRow
                index={i}
                title={title}
                place={place}
                period={period}
                sketch={i === 1 ? "firmensitz" : "haus"}
              />
            </Reveal>
          ))}
        </div>

        {r.summary && (
          <Reveal delay={0.15}>
            <div
              className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-2 gap-10 md:gap-16 border-t border-[var(--line)] pt-12 md:pt-16 md:pl-[3.25rem]"
              data-testid="references-summary"
            >
              {r.summary.map(([value, label], i) => (
                <div key={i} className="flex items-baseline gap-4" data-testid={`reference-stat-${i}`}>
                  <span className="font-heading font-bold tracking-tight text-5xl md:text-7xl text-[var(--accent)] leading-none">
                    {value}
                  </span>
                  <span className="font-body font-light text-sm md:text-base text-[var(--txt-dim)] max-w-[180px] leading-snug">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
